"use client";
/**
 * Настройки (/settings):
 *  - Тіп: светлая / тёмная (профессиональная, без анимаций)
 *  - Баптаулар: тіл, лаборатория OS
 *  - Қауіпсіздік: барлық құрылғыдан шығу
 */
import { useEffect, useState } from "react";
import Link from "next/link";
import {
  Check,
  Cpu,
  KeyRound,
  Languages,
  LogOut,
  MonitorSmartphone,
  Palette,
  Terminal,
} from "lucide-react";
import { useAuth } from "@/lib/auth/provider";
import { useApi } from "@/lib/hooks/use-api";
import { api } from "@/lib/api/client";
import { useI18n } from "@/lib/i18n";
import {
  BG_DEFAULT,
  BG_THEMES,
  getBgTheme,
  setBgTheme,
  setNightTheme,
  type BgThemeId,
} from "@/lib/themes";
import {
  Badge,
  Button,
  PageHeading,
} from "@/components/ui";
import { RequireAuth } from "@/components/auth";
import type { User } from "@/lib/types";

const PREVIEWS: Record<BgThemeId, string> = {
  light: "bg-prev bg-prev-light",
  dark: "bg-prev bg-prev-dark",
};

export function SettingsPage() {
  const { t, lang, setLang } = useI18n();
  const auth = useAuth();
  const [theme, setTheme] = useState<BgThemeId>(
    typeof window === "undefined" ? BG_DEFAULT : getBgTheme(),
  );
  const [os, setOs] = useState<string>("kali");
  const [osMsg, setOsMsg] = useState<{ ok: boolean; text: string }>();
  const [allBusy, setAllBusy] = useState(false);
  const [allMsg, setAllMsg] = useState<{ ok: boolean; text: string }>();
  const [me, setMe] = useState<User | null>(null);

  useEffect(() => {
    setTheme(getBgTheme());
    const onBg = (e: Event) =>
      setTheme((e as CustomEvent<BgThemeId>).detail);
    window.addEventListener("cyberalem:bg", onBg);
    return () => window.removeEventListener("cyberalem:bg", onBg);
  }, []);

  useEffect(() => {
    if (auth.user?.lab_os) setOs(auth.user.lab_os);
  }, [auth.user?.lab_os]);

  async function loadMe() {
    try {
      setMe(await api<User>("/users/me"));
    } catch {
      /* guest mode — профиль карточкасы жасырылады */
    }
  }
  useEffect(() => {
    if (auth.user) void loadMe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.user?.id]);

  function pick(id: BgThemeId) {
    if (id === "dark") setNightTheme("dark"); // күндіз/түн батырмасы түнгі режимде осы қолданады
    setBgTheme(id); // localStorage + CustomEvent → тема live ауысады
  }

  async function saveOs(v: string) {
    setOs(v);
    setOsMsg(undefined);
    try {
      await api("/users/me/lab-os", {
        method: "POST",
        body: JSON.stringify({ os: v }),
      });
      setOsMsg({ ok: true, text: t("profile.os_saved") });
    } catch (e) {
      setOsMsg({ ok: false, text: (e as Error).message });
    }
  }

  async function logoutAll() {
    if (!window.confirm(t("profile.logout_all_warn"))) return;
    setAllBusy(true);
    setAllMsg(undefined);
    try {
      await api("/users/me/logout-all", { method: "POST" });
      setAllMsg({ ok: true, text: t("profile.logout_all_done") });
      setTimeout(() => auth.logout(), 1200);
    } catch (e) {
      setAllMsg({ ok: false, text: (e as Error).message });
    } finally {
      setAllBusy(false);
    }
  }

  return (
    <>
      <PageHeading
        title={t("settings.title")}
        description={t("settings.desc")}
      />
      <div className="settings-layout">
        <section className="panel">
          <h3 className="account-card-title">
            <Palette size={16} /> {t("settings.bg_title")}
          </h3>
          <div className="bg-pick-grid">
            {BG_THEMES.map((th) => {
              const active = theme === th.id;
              return (
                <button
                  key={th.id}
                  type="button"
                  className={`bg-pick-card${active ? " active" : ""}`}
                  onClick={() => pick(th.id)}
                  aria-pressed={active}
                >
                  <span className={PREVIEWS[th.id]} aria-hidden="true" />
                  <span className="bg-pick-name">
                    {t(th.key)}
                    {th.id === BG_DEFAULT && (
                      <em className="bg-pick-default">
                        {t("settings.bg_default")}
                      </em>
                    )}
                  </span>
                  <span className="bg-pick-desc">{t(`${th.key}_d`)}</span>
                  {active && (
                    <span className="bg-pick-check">
                      <Check size={14} />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          <p className="muted small settings-hint">
            {t("settings.bg_hint")}
          </p>
        </section>

        <div className="settings-cols">
          <section className="panel">
            <h3 className="account-card-title">
              <Cpu size={16} /> {t("settings.prefs_title")}
            </h3>
            <div className="settings-block">
              <label className="field-label">
                <Languages size={13} /> {t("profile.lang_label")}
              </label>
              <select
                className="input"
                value={lang}
                onChange={(e) =>
                  setLang(e.target.value as "kk" | "ru" | "en" | "ko")
                }
              >
                <option value="kk">Қазақша</option>
                <option value="ru">Русский</option>
                <option value="en">English</option>
                <option value="ko">한국어</option>
              </select>
            </div>
            <div className="settings-block">
              <label className="field-label">
                <MonitorSmartphone size={13} /> {t("profile.labos_label")}
              </label>
              <select
                className="input"
                value={os}
                disabled={!auth.user}
                onChange={(e) => void saveOs(e.target.value)}
              >
                <option value="kali">Kali Linux</option>
                <option value="parrot">Parrot OS</option>
                <option value="ubuntu">Ubuntu 24.04</option>
              </select>
              {osMsg && (
                <p className={osMsg.ok ? "form-ok" : "form-error"}>
                  {osMsg.text}
                </p>
              )}
            </div>
          </section>

          <section className="panel">
            <h3 className="account-card-title">
              <KeyRound size={16} /> {t("profile.security_title")}
            </h3>
            <div className="settings-block">
              <label className="field-label">
                {t("profile.pw_label")}
              </label>
              <Link className="button secondary" href="/profile">
                {t("profile.change_pw")} <KeyRound size={15} />
              </Link>
            </div>
            {auth.user && (
              <div className="settings-block">
                <label className="field-label">
                  {t("profile.sessions_title")}
                </label>
                <Button
                  variant="secondary"
                  busy={allBusy}
                  onClick={() => void logoutAll()}
                >
                  <LogOut size={15} /> {t("profile.logout_all")}
                </Button>
                {allMsg && (
                  <p className={allMsg.ok ? "form-ok" : "form-error"}>
                    {allMsg.text}
                  </p>
                )}
              </div>
            )}
          </section>
        </div>

        {auth.user && me && (
          <section className="panel">
            <h3 className="account-card-title">
              <Terminal size={16} /> {t("settings.about_title")}
            </h3>
            <div className="settings-about">
              <div className="inline" style={{ gap: 10 }}>
                <span className="avatar small">{me.name.charAt(0)}</span>
                <div>
                  <strong>{me.name}</strong>
                  <p className="muted small">{me.email}</p>
                </div>
                <Badge value={me.role} />
              </div>
              <dl className="details">
                <div>
                  <dt>{t("profile.registered")}</dt>
                  <dd>{new Date(me.registered_at).toLocaleDateString()}</dd>
                </div>
                <div>
                  <dt>{t("profile.group")}</dt>
                  <dd>{me.group ?? "—"}</dd>
                </div>
                <div>
                  <dt>{t("settings.platform")}</dt>
                  <dd>CyberAlem 2.0</dd>
                </div>
              </dl>
            </div>
          </section>
        )}
      </div>
    </>
  );
}
